import { useEffect, useState } from 'react';
import { usePedidosCozinha } from '../../hooks/usePedidosCozinha';
import { useAuth } from '../../contexts/AuthContext';
import { orderService, categoryService, Product } from '../../services';
import TempoDeEspera from '../../components/TempoDeEspera';
import ModalMotivoCancelamento from '../../components/ModalMotivoCancelamento';
import ModalConfirmacao from '../../components/ModalConfirmacao';
import { imprimirComanda } from '../../utils/comanda';
import { tempoDecorrido } from '../../utils/tempo';
import { usePagination } from '../../hooks/usePagination';
import PaginationControls from '../../components/ui/PaginationControls';
import type { Order } from '../../services';

/** Status anterior: a "setinha voltar" desfaz um passo do fluxo (erro de clique) */
const STATUS_PREV: Record<string, string> = {
  CONFIRMED: 'PENDING',
  PREPARING: 'CONFIRMED',
  READY: 'PREPARING',
  DELIVERED: 'READY',
  REQUESTED_BILL: 'DELIVERED',
};

export const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  PENDING: { label: 'Pendente', color: 'bg-yellow-900/60 text-yellow-300' },
  CONFIRMED: { label: 'Confirmado', color: 'bg-blue-900/60 text-blue-300' },
  PREPARING: { label: 'Em Preparo', color: 'bg-orange-900/60 text-orange-300' },
  READY: { label: 'Pronto', color: 'bg-green-900/60 text-green-300' },
  DELIVERED: { label: 'Entregue', color: 'bg-teal-900/60 text-teal-300' },
  PAID: { label: 'Pago', color: 'bg-emerald-900/60 text-emerald-300' },
  CANCELLED: { label: 'Cancelado', color: 'bg-red-900/60 text-red-300' },
  REQUESTED_BILL: { label: 'Conta, por favor', color: 'bg-orange-600/80 text-white' },
};

/** Status que são trabalho da cozinha (exibidos por padrão) */
export const STATUS_COZINHA = ['PENDING', 'CONFIRMED', 'PREPARING', 'READY'];

/**
 * Tela única de Pedidos: cada perfil vê o que é da sua responsabilidade
 * (cozinha prepara, garçom entrega/fecha, admin tudo). Traz os dois layouts
 * (cards/lista), tela cheia, impressão e edição de pedido — incluindo o
 * antigo "modo TV": basta entrar em tela cheia (⛶).
 */
export default function Orders() {
  const { perfilRestaurante, user } = useAuth();
  // Perfil efetivo DENTRO do restaurante (vínculo); legado global só como fallback
  const perfil = perfilRestaurante ?? user?.role ?? '';
  const ehCozinha = perfil === 'COZINHA' || perfil === 'WAITER';
  const ehGarcom = perfil === 'GARCOM';
  const ehAdmin = perfil === 'SUPER_ADMIN' || perfil === 'ADMIN';
  const podeEditarCancelar = ehAdmin || ehGarcom;

  // Abas: Fila (operações) vs Histórico (pagos/cancelados — só admin)
  const [aba, setAba] = useState<'fila' | 'historico'>('fila');
  const [menuAberto, setMenuAberto] = useState<string | null>(null);

  // Controle por status na Fila: 2 grupos — Pendente (ainda não concluído) e
  // Concluído (já entregue/pago). Padrão = Pendente.
  const [filtroFila, setFiltroFila] = useState<'pendente' | 'concluido'>('pendente');
  const filaVisiveis = ehCozinha
    ? ['PENDING', 'CONFIRMED', 'PREPARING', 'READY']
    : filtroFila === 'concluido'
      ? ['DELIVERED', 'PAID']
      : ehGarcom
        ? ['PENDING', 'READY', 'REQUESTED_BILL']
        : ['PENDING', 'CONFIRMED', 'PREPARING', 'READY', 'REQUESTED_BILL'];
  const statusVisiveis = aba === 'fila' ? filaVisiveis : ['PAID', 'CANCELLED'];

  const { orders, isLoading, setFilterStatus, atualizarStatus, erroConexao, erroAcao, limparErroAcao, recarregar, registrarErroAcao } =
    usePedidosCozinha();

  // Histórico: busca própria paginada (cresce com o tempo) — mais recentes primeiro
  const historicoPg = usePagination({ defaultLimit: 20 });
  const [historicoOrders, setHistoricoOrders] = useState<Order[]>([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);

  useEffect(() => {
    if (aba !== 'historico') return;
    let cancelado = false;
    setCarregandoHistorico(true);
    orderService
      .findAll({
        page: historicoPg.page,
        limit: historicoPg.limit,
        status: 'PAID,CANCELLED',
        ordenar: 'desc',
      })
      .then((r) => {
        if (cancelado) return;
        setHistoricoOrders(r.data.data);
        historicoPg.syncFromResponse(r.data);
      })
      .catch((e) => console.error('Erro ao carregar histórico:', e))
      .finally(() => {
        if (!cancelado) setCarregandoHistorico(false);
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aba, historicoPg.page, historicoPg.limit, historicoPg.syncFromResponse]);

  // Busca no backend só o que o grupo atual precisa exibir (Fila = ao vivo,
  // sem paginação — o histórico cuida do que já passou). REQUESTED_BILL
  // fica no grupo Pendente (badge laranja + alerta sonoro quando o cliente
  // pede a conta). PAID entra no Concluído (além do Histórico).
  useEffect(() => {
    if (aba === 'historico') {
      return;
    }
    const grupo = ehCozinha
      ? 'PENDING,CONFIRMED,PREPARING,READY'
      : filtroFila === 'concluido'
        ? 'DELIVERED,PAID'
        : ehGarcom
          ? 'PENDING,READY,REQUESTED_BILL'
          : 'PENDING,CONFIRMED,PREPARING,READY,REQUESTED_BILL';
    setFilterStatus(grupo);
  }, [aba, filtroFila, ehCozinha]);

  // Layout (cards ou lista) — escolha fica salva no aparelho
  const [layout, setLayout] = useState<'cards' | 'lista'>(
    () => (localStorage.getItem('cardapio:pedidos:layout') as 'cards' | 'lista') || 'cards',
  );
  const trocarLayout = (novo: 'cards' | 'lista') => {
    setLayout(novo);
    localStorage.setItem('cardapio:pedidos:layout', novo);
  };

  // Tela cheia (modo TV/preparador de parede)
  const [telaCheia, setTelaCheia] = useState(false);
  useEffect(() => {
    const aoMudar = () => setTelaCheia(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', aoMudar);
    return () => document.removeEventListener('fullscreenchange', aoMudar);
  }, []);
  const alternarTelaCheia = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        const el = document.documentElement as any;
        const pedido = el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.();
        if (pedido?.catch) pedido.catch(() => undefined);
      }
    } catch {
      // navegador sem suporte (ex.: iPhone pelo Safari)
    }
  };

  // T114: cancelamento pede motivo (vai para o log de auditoria — T140)
  const [cancelandoPedido, setCancelandoPedido] = useState<Order | null>(null);
  const confirmarCancelamento = async (motivo: string) => {
    if (!cancelandoPedido) return;
    try {
      await orderService.updateStatus(cancelandoPedido.id, { status: 'CANCELLED', motivo });
      setCancelandoPedido(null);
      recarregar();
    } catch {
      registrarErroAcao('Não foi possível cancelar o pedido. Tente novamente.');
    }
  };

  // T106: cancelou por engano — reabrir a comanda (enquanto não houver pagamento)
  const [reabrindoPedido, setReabrindoPedido] = useState<Order | null>(null);
  const confirmarReabertura = async () => {
    if (!reabrindoPedido) return;
    try {
      await orderService.updateStatus(reabrindoPedido.id, { status: 'PENDING' });
      setReabrindoPedido(null);
      recarregar();
    } catch (error) {
      setReabrindoPedido(null);
      registrarErroAcao(
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Não foi possível reabrir o pedido.',
      );
    }
  };

  /** Linhas de resumo usadas nas confirmações (cancelar/reabrir) */
  const resumoPedido = (order: Order) => {
    const itens = (order.items ?? []).filter((i) => !i.cancelado);
    return [
      `👤 ${order.customer_name}`,
      order.table_number ? `🪑 Mesa ${order.table_number}` : '🛒 Balcão',
      `🧾 ${itens.length} item(ns): ${itens.map((i) => `${i.quantity}x ${i.product?.name}`).join(', ') || '—'}`,
      `💰 Total R$ ${Number(order.total).toFixed(2)}`,
    ];
  };

  // Edição de pedido (garçom/admin): adicionar itens a um pedido existente
  const [editandoPedido, setEditandoPedido] = useState<Order | null>(null);
  // Criar pedido novo para mesa/balcão (garçom/admin)
  const [novaComanda, setNovaComanda] = useState<{ mesa: string; nome: string } | null>(null);
  const [buscaProdutos, setBuscaProdutos] = useState('');
  const [produtos, setProdutos] = useState<Product[]>([]);
  const [selecao, setSelecao] = useState<Record<string, number>>({});
  const [carregandoProdutos, setCarregandoProdutos] = useState(false);
  const [erroEdicao, setErroEdicao] = useState('');

  const abrirEdicao = async (order: Order) => {
    setEditandoPedido(order);
    setBuscaProdutos('');
    setSelecao({});
    setErroEdicao('');
    await carregarProdutos('');
  };

  const abrirNovaComanda = async () => {
    setNovaComanda({ mesa: '', nome: '' });
    setBuscaProdutos('');
    setSelecao({});
    setErroEdicao('');
    await carregarProdutos('');
  };

  const criarNovaComanda = async () => {
    const itens = Object.entries(selecao)
      .filter(([, qtd]) => qtd > 0)
      .map(([product_id, quantity]) => ({ product_id, quantity }));
    if (itens.length === 0) {
      setErroEdicao('Selecione pelo menos um item');
      return;
    }
    try {
      const mesaNumero = novaComanda!.mesa.trim()
        ? parseInt(novaComanda!.mesa.trim(), 10)
        : undefined;
      await orderService.create({
        customer_name:
          novaComanda!.nome.trim() || (mesaNumero ? `Mesa ${mesaNumero}` : 'Balcão'),
        table_number: mesaNumero,
        items: itens,
        lancado_por: 'garcom' as const,
      });
      setNovaComanda(null);
    } catch (error) {
      const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setErroEdicao(msg || 'Erro ao criar a comanda. Tente novamente.');
    }
  };

  const carregarProdutos = async (busca: string) => {
    setCarregandoProdutos(true);
    try {
      const r = await categoryService.findAllActive();
      const normalizada = busca
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
      const todos = r.data.data.flatMap((cat) => cat.products);
      setProdutos(
        normalizada
          ? todos.filter(
              (p) =>
                p.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(normalizada) ||
                (p.description ?? '')
                  .toLowerCase()
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .includes(normalizada),
            )
          : todos,
      );
    } catch {
      setProdutos([]);
    } finally {
      setCarregandoProdutos(false);
    }
  };

  const adicionarItemNoPedido = async () => {
    const itens = Object.entries(selecao)
      .filter(([, qtd]) => qtd > 0)
      .map(([product_id, quantity]) => ({ product_id, quantity }));
    if (itens.length === 0) {
      setErroEdicao('Selecione pelo menos um item');
      return;
    }
    try {
      await orderService.create({
        customer_name: editandoPedido!.customer_name,
        table_number: editandoPedido!.table_number ?? undefined,
        items: itens,
        merge_into_order_id: editandoPedido!.id,
        adicionar_itens: true,
        lancado_por: 'garcom' as const,
      });
      setEditandoPedido(null);
    } catch {
      setErroEdicao('Erro ao adicionar itens. Tente novamente.');
    }
  };

  if (isLoading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  /** Ações do pedido: UM botão principal (a próxima ação) + menu "⋯" com o resto */
  const acoesDoCard = (order: Order) => {
    interface Acao {
      chave: string;
      rotulo: string;
      fazer: () => void;
      principal?: boolean;
      perigo?: boolean;
    }

    const acoes: Acao[] = [];

    // Ação natural de produção (cozinha — ou admin assumindo)
    if (ehAdmin || ehCozinha) {
      if (order.status === 'PENDING') {
        acoes.push({ chave: 'confirmar', rotulo: 'Confirmar', principal: true, fazer: () => atualizarStatus(order.id, 'CONFIRMED') });
      } else if (order.status === 'CONFIRMED') {
        acoes.push({ chave: 'preparar', rotulo: 'Iniciar Preparo', principal: true, fazer: () => atualizarStatus(order.id, 'PREPARING') });
      } else if (order.status === 'PREPARING') {
        acoes.push({ chave: 'pronto', rotulo: 'Marcar como Pronto', principal: true, fazer: () => atualizarStatus(order.id, 'READY') });
      }
    }
    // Ação natural de entrega/conta (garçom — ou admin assumindo)
    if (ehAdmin || ehGarcom) {
      if (order.status === 'READY') {
        acoes.push({ chave: 'entregar', rotulo: '🚚 Marcar como Entregue', principal: true, fazer: () => atualizarStatus(order.id, 'DELIVERED') });
      }
    }
    // Ações secundárias (sempre no menu ⋯)
    if (podeEditarCancelar && ['PENDING', 'CONFIRMED', 'PREPARING', 'READY'].includes(order.status)) {
      acoes.push({ chave: 'editar', rotulo: '✏️ Editar', fazer: () => abrirEdicao(order) });
      acoes.push({ chave: 'cancelar', rotulo: '✕ Cancelar', perigo: true, fazer: () => setCancelandoPedido(order) });
    }
    // Voltar um passo (erro de clique): quem pode avançar também desfaz
    const prevStatus = STATUS_PREV[order.status];
    const podeVoltar =
      !!prevStatus &&
      (ehAdmin || ehGarcom || (ehCozinha && ['CONFIRMED', 'PREPARING'].includes(order.status)));
    if (podeVoltar) {
      acoes.push({
        chave: 'voltar',
        rotulo: '↩ Voltar um passo',
        fazer: () => atualizarStatus(order.id, prevStatus),
      });
    }
    acoes.push({ chave: 'imprimir', rotulo: '🖨️ Imprimir', fazer: () => imprimirComanda(order) });
    // T106: pedido cancelado por engano tem caminho de volta (enquanto não foi pago)
    if (order.status === 'CANCELLED' && (ehAdmin || ehGarcom)) {
      acoes.push({
        chave: 'reabrir',
        rotulo: '↩ Reabrir pedido',
        fazer: () => setReabrindoPedido(order),
      });
    }

    // Casos sem botão de ação (a cozinha não entrega; o cliente espera o garçom)
    if (order.status === 'READY' && !(ehAdmin || ehGarcom)) {
      return <p className="text-sm text-gray-400">Pronto — aguardando o garçom levar à mesa</p>;
    }
    if ((order.status === 'DELIVERED' || order.status === 'REQUESTED_BILL') && (ehAdmin || ehGarcom)) {
      return (
        <div className="flex flex-wrap items-center gap-2">
          <p className={order.status === 'REQUESTED_BILL' ? 'text-sm text-orange-300' : 'text-sm text-green-400'}>
            {order.status === 'REQUESTED_BILL' ? '🧾 Conta solicitada - fechar na aba Comandas' : '☑️ Entregue - fechar na aba Comandas'}
          </p>
        </div>
      );
    }

    const principal = acoes.find((a) => a.principal);
    const secundarias = acoes.filter((a) => !a.principal);

    return (
      <div className="flex flex-wrap items-center gap-2">
        {principal && (
          <button key={principal.chave} onClick={principal.fazer} className="btn-primary text-sm">
            {principal.rotulo}
          </button>
        )}
        {secundarias.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setMenuAberto(menuAberto === order.id ? null : order.id)}
              className="btn-secondary text-sm px-2"
              aria-label="Mais ações"
            >
              ⋯
            </button>
            {menuAberto === order.id && (
              <div className="absolute right-0 z-50 mt-1 w-44 bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-1 space-y-1">
                {secundarias.map((a) => (
                  <button
                    key={a.chave}
                    onClick={() => {
                      a.fazer();
                      setMenuAberto(null);
                    }}
                    className={`w-full text-left text-sm px-3 py-2 rounded ${
                      a.perigo
                        ? 'text-red-400 hover:bg-red-900/30'
                        : 'text-gray-200 hover:bg-gray-700/60'
                    }`}
                  >
                    {a.rotulo}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Filtro aplicado conforme a aba ativa (histórico chega paginado do backend)
  const pedidosVisiveis =
    aba === 'historico'
      ? historicoOrders
      : orders.filter((o) => statusVisiveis.includes(o.status));

  const atrasado = (order: Order) =>
    ['PENDING', 'CONFIRMED', 'PREPARING'].includes(order.status) &&
    Math.floor((Date.now() - new Date(order.created_at).getTime()) / 60000) >= 20;

  return (
    <div>
      {/* T117: falha de polling e erro de ação aparecem na tela (sem alert nativo) */}
      {erroConexao && (
        <div className="card mb-4 border border-amber-500/40 bg-amber-900/10">
          <p className="text-sm text-amber-300">
            ⚠️ Sem conexão com o servidor — a fila pode estar desatualizada. Tentando novamente…
          </p>
        </div>
      )}
      {erroAcao && (
        <div className="card mb-4 border border-red-500/40 bg-red-900/10 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-red-300">{erroAcao}</p>
          <button onClick={limparErroAcao} className="btn-secondary text-sm">
            Fechar
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-bold text-gray-100">📋 Pedidos</h1>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setAba('fila')}
            className={`text-sm ${aba === 'fila' ? 'btn-primary' : 'btn-secondary'}`}
          >
            📋 Fila
          </button>
          {ehAdmin && (
            <button
              onClick={() => setAba('historico')}
              className={`text-sm ${
                aba === 'historico' ? 'btn-primary' : 'btn-secondary'
              }`}
            >
              🕘 Histórico
            </button>
          )}
          <button
            onClick={() => trocarLayout('cards')}
            className={`btn-secondary text-sm hidden md:inline-flex ${layout === 'cards' ? 'border-primary-500/60' : 'opacity-50'}`}
          >
            ▦ Cards
          </button>
          <button
            onClick={() => trocarLayout('lista')}
            className={`btn-secondary text-sm hidden md:inline-flex ${layout === 'lista' ? 'border-primary-500/60' : 'opacity-50'}`}
          >
            ☰ Lista
          </button>
          <button
            onClick={alternarTelaCheia}
            className="btn-secondary text-sm hidden md:inline-flex"
          >
            {telaCheia ? '⛶ Sair da tela cheia' : '⛶ Tela cheia'}
          </button>
          {(ehAdmin || ehGarcom) && (
            <button
              onClick={abrirNovaComanda}
              className="btn-primary text-sm"
            >
              ➕ Novo pedido
            </button>
          )}
        </div>
      </div>

      {aba === 'fila' && (ehAdmin || ehGarcom) && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            onClick={() => setFiltroFila('pendente')}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
              filtroFila === 'pendente'
                ? 'border-primary-500/60 bg-primary-600/15 text-primary-400'
                : 'border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-200'
            }`}
          >
            📋 Pendente
          </button>
          <button
            onClick={() => setFiltroFila('concluido')}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
              filtroFila === 'concluido'
                ? 'border-primary-500/60 bg-primary-600/15 text-primary-400'
                : 'border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-200'
            }`}
          >
            ✅ Concluído
          </button>
        </div>
      )}

      {pedidosVisiveis.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-400">
            {aba === 'historico'
              ? 'Nenhum pedido no histórico'
              : filtroFila === 'concluido'
                ? 'Nenhum pedido concluído'
                : 'Nenhum pedido pendente 🎉'}
          </p>
        </div>
      ) : layout === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {pedidosVisiveis.map((order) => (
            <div
              key={order.id}
              className={`card p-5 ${atrasado(order) ? 'border-red-700/70 bg-red-950/20' : ''}`}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-2xl font-bold text-gray-100">{order.customer_name}</h3>
                  {order.table_number ? (
                    <p className="text-3xl font-extrabold text-primary-400 mt-1">
                      MESA {order.table_number}
                    </p>
                  ) : (
                    <p className="text-lg text-gray-400 mt-1">Balcão</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(order.created_at).toLocaleString('pt-BR')}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_LABELS[order.status].color}`}>
                    {STATUS_LABELS[order.status].label}
                  </span>
                  <div className="mt-2">
                    <TempoDeEspera criadoEm={order.created_at} />
                  </div>
                  {/* T118: itens do "pedir mais" têm tempo próprio — o tempo do
                      pedido não pode fazer o item novo parecer antigo */}
                  {(() => {
                    const lotes = order.items
                      .filter((i) => !i.cancelado)
                      .map((i) => i.lote ?? 1);
                    const ultimoLote = lotes.length ? Math.max(...lotes) : 1;
                    if (ultimoLote <= 1) return null;
                    const novos = order.items.filter(
                      (i) => !i.cancelado && (i.lote ?? 1) === ultimoLote,
                    );
                    const quando =
                      novos
                        .map((i) => i.lancado_em)
                        .filter((d): d is string => Boolean(d))
                        .sort()[0] ?? order.created_at;
                    return (
                      <p
                        className="text-xs text-sky-300 mt-1"
                        title="Itens adicionados depois, pelo “Pedir mais”"
                      >
                        ➕ itens novos há {tempoDecorrido(quando)}
                      </p>
                    );
                  })()}
                  <p className="text-lg font-bold text-primary-400 mt-1">
                    R$ {Number(order.total).toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="border-t pt-3 mb-3">
                {order.items.map((item) => (
                  <div key={item.id} className="flex justify-between text-base py-0.5">
                    <span className="text-gray-200">
                      {item.quantity}x {item.product?.name}
                      {item.product?.exige_cozinha === false && (
                        <span className="ml-1 text-xs text-sky-300 bg-sky-900/30 px-1.5 py-0.5 rounded">
                          🧃 balcão
                        </span>
                      )}
                      {item.notes && <span className="text-gray-400"> ({item.notes})</span>}
                    </span>
                    <span className="text-gray-400">R$ {Number(item.subtotal).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {order.notes && (
                <div className="bg-gray-700/50 p-3 rounded-lg mb-3">
                  <p className="text-sm text-gray-300">
                    <strong>Observações:</strong> {order.notes}
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-2">{acoesDoCard(order)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-700 text-sm text-gray-400">
                <th className="p-3">Mesa</th>
                <th className="p-3">Cliente</th>
                <th className="p-3">Itens</th>
                <th className="p-3">Total</th>
                <th className="p-3">Status</th>
                <th className="p-3">Espera</th>
                <th className="p-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {pedidosVisiveis.map((order) => (
                <tr
                  key={order.id}
                  className={`border-b border-gray-800 ${atrasado(order) ? 'bg-red-950/20' : ''}`}
                >
                  <td className="p-3 font-bold text-primary-400">
                    {order.table_number ? `MESA ${order.table_number}` : 'Balcão'}
                  </td>
                  <td className="p-3 font-semibold text-gray-100">{order.customer_name}</td>
                  <td className="p-3 text-sm text-gray-300">
                    {order.items.map((i) => `${i.quantity}x ${i.product?.name}`).join(', ')}
                  </td>
                  <td className="p-3 font-semibold text-primary-400">
                    R$ {Number(order.total).toFixed(2)}
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_LABELS[order.status].color}`}>
                      {STATUS_LABELS[order.status].label}
                    </span>
                  </td>
                  <td className="p-3 text-sm">
                    <TempoDeEspera criadoEm={order.created_at} />
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1.5 max-w-md">{acoesDoCard(order)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {aba === 'historico' && (
        <div className="mt-4">
          {carregandoHistorico && (
            <p className="text-center text-sm text-gray-500 mb-2">Carregando histórico...</p>
          )}
          <PaginationControls
            currentPage={historicoPg.page}
            totalPages={historicoPg.totalPages}
            totalItems={historicoPg.totalItems}
            itemsPerPage={historicoPg.limit}
            onPageChange={historicoPg.setPage}
            onLimitChange={historicoPg.setLimit}
            label="pedidos"
          />
        </div>
      )}

      {editandoPedido && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="card w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h2 className="text-xl font-bold text-gray-100">✏️ Editar pedido</h2>
                <p className="text-sm text-gray-400">
                  {editandoPedido.customer_name}
                  {editandoPedido.table_number ? ` · Mesa ${editandoPedido.table_number}` : ''} — os
                  itens escolhidos serão somados ao pedido
                </p>
              </div>
              <button
                onClick={() => setEditandoPedido(null)}
                className="text-gray-400 hover:text-gray-200 text-xl"
              >
                ✕
              </button>
            </div>

            <input
              type="text"
              value={buscaProdutos}
              onChange={(e) => {
                setBuscaProdutos(e.target.value);
                carregarProdutos(e.target.value);
              }}
              placeholder="🔍 Buscar prato..."
              className="input-field mb-3"
            />

            <div className="space-y-2 mb-4">
              {carregandoProdutos && <p className="text-sm text-gray-500">Buscando...</p>}
              {produtos.length === 0 && !carregandoProdutos && (
                <p className="text-sm text-gray-500">Nenhum produto encontrado</p>
              )}
              {produtos.map((produto) => (
                <div
                  key={produto.id}
                  className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-lg p-2.5"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-100 truncate">{produto.name}</p>
                    <p className="text-sm text-gray-400">R$ {Number(produto.price).toFixed(2)}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {produto.is_available === false ? (
                      <span className="text-xs text-red-400">Esgotado</span>
                    ) : (
                      <>
                        <button
                          onClick={() =>
                            setSelecao((anterior) => ({
                              ...anterior,
                              [produto.id]: Math.max(0, (anterior[produto.id] ?? 0) - 1),
                            }))
                          }
                          className="btn-secondary text-sm px-2 py-1"
                        >
                          −
                        </button>
                        <span className="w-6 text-center text-gray-100">{selecao[produto.id] ?? 0}</span>
                        <button
                          onClick={() =>
                            setSelecao((anterior) => ({
                              ...anterior,
                              [produto.id]: (anterior[produto.id] ?? 0) + 1,
                            }))
                          }
                          className="btn-secondary text-sm px-2 py-1"
                        >
                          +
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {erroEdicao && <p className="text-red-400 text-sm mb-2">{erroEdicao}</p>}

            <button onClick={adicionarItemNoPedido} className="btn-primary w-full">
              ➕ Adicionar ao pedido
            </button>
          </div>
        </div>
      )}
    {novaComanda && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="card w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h2 className="text-xl font-bold text-gray-100">➕ Novo pedido</h2>
                <p className="text-sm text-gray-400">
                  Crie um pedido para uma mesa ou balcão — selecione os itens e envie
                </p>
              </div>
              <button
                onClick={() => setNovaComanda(null)}
                className="text-gray-400 hover:text-gray-200 text-xl"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Mesa</label>
                <input
                  type="number"
                  value={novaComanda.mesa}
                  onChange={(e) => setNovaComanda({ ...novaComanda, mesa: e.target.value })}
                  placeholder="Nº da mesa"
                  className="input-field"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Cliente</label>
                <input
                  type="text"
                  value={novaComanda.nome}
                  onChange={(e) => setNovaComanda({ ...novaComanda, nome: e.target.value })}
                  placeholder="Nome (opcional)"
                  className="input-field"
                />
              </div>
            </div>

            <input
              type="text"
              value={buscaProdutos}
              onChange={(e) => {
                setBuscaProdutos(e.target.value);
                carregarProdutos(e.target.value);
              }}
              placeholder="🔍 Buscar prato..."
              className="input-field mb-3"
            />

            <div className="space-y-2 mb-4">
              {carregandoProdutos && <p className="text-sm text-gray-500">Buscando...</p>}
              {produtos.length === 0 && !carregandoProdutos && (
                <p className="text-sm text-gray-500">Nenhum produto encontrado</p>
              )}
              {produtos.map((produto) => (
                <div
                  key={produto.id}
                  className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-lg p-2.5"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-100 truncate">{produto.name}</p>
                    <p className="text-sm text-gray-400">R$ {Number(produto.price).toFixed(2)}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {produto.is_available === false ? (
                      <span className="text-xs text-red-400">Esgotado</span>
                    ) : (
                      <>
                        <button
                          onClick={() =>
                            setSelecao((anterior) => ({
                              ...anterior,
                              [produto.id]: Math.max(0, (anterior[produto.id] ?? 0) - 1),
                            }))
                          }
                          className="btn-secondary text-sm px-2 py-1"
                        >
                          −
                        </button>
                        <span className="w-6 text-center text-gray-100">{selecao[produto.id] ?? 0}</span>
                        <button
                          onClick={() =>
                            setSelecao((anterior) => ({
                              ...anterior,
                              [produto.id]: (anterior[produto.id] ?? 0) + 1,
                            }))
                          }
                          className="btn-secondary text-sm px-2 py-1"
                        >
                          +
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {erroEdicao && <p className="text-red-400 text-sm mb-2">{erroEdicao}</p>}

            <button onClick={criarNovaComanda} className="btn-primary w-full">
              ✅ Criar pedido
            </button>
          </div>
        </div>
      )}

      {/* T114: motivo do cancelamento (registrado com o autor) */}
      {cancelandoPedido && (
        <ModalMotivoCancelamento
          titulo={`Cancelar pedido de ${cancelandoPedido.customer_name}`}
          resumo={resumoPedido(cancelandoPedido)}
          onConfirmar={confirmarCancelamento}
          onCancelar={() => setCancelandoPedido(null)}
        />
      )}

      {/* T106: reabrir comanda cancelada — com resumo do que estava no pedido */}
      {reabrindoPedido && (
        <ModalConfirmacao
          titulo="↩ Reabrir este pedido?"
          subtitulo="Ele volta para a fila como Pendente (e o estoque é baixado de novo)."
          resumo={[
            ...resumoPedido(reabrindoPedido),
            reabrindoPedido.motivo_cancelamento
              ? `✕ Cancelado por: ${reabrindoPedido.motivo_cancelamento}`
              : '✕ Cancelado sem motivo registrado',
          ]}
          aviso="A reabertura fica registrada no log de auditoria com o seu usuário."
          rotuloConfirmar="Reabrir pedido"
          onConfirmar={confirmarReabertura}
          onCancelar={() => setReabrindoPedido(null)}
        />
      )}
    </div>
  );
}